import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getCurrentTableParams } from "data/transaction/transactionTableSelector";
import authApi from "interceptor/authInterceptor";
import { get } from "lodash";
import toast from "utils/ToastNotistack";
import T from "utils/constants/T";
import { PAGINATION } from "utils/constants/pagination";
const { INITIAL_PAGE, ROWS_PER_PAGE } = PAGINATION;

// types
export type deductionDataProps = {
  deductionTableList: any[];
  deductionViewList: any[];
  deductionExportTableList: any[];
  totalDeductionItems: number;
  totalViewDeductionItems: number;
  totalViewDeductionPages: number;
  totalDeductionPages: number;
  totalDeductionExportPages: number;
  viewPageNo: number;
  viewPageSize: number;
  pageNo: number;
  pageSize: number;
  loader: boolean;
  activeRequestId: string | null;
  error: null;
};

// initial state
const initialState: deductionDataProps = {
  deductionTableList: [],
  deductionViewList: [],
  deductionExportTableList: [],
  totalViewDeductionItems: 10,
  totalDeductionItems: 10,
  totalViewDeductionPages: 1,
  totalDeductionPages: 10,
  totalDeductionExportPages: 0,
  viewPageNo: INITIAL_PAGE,
  viewPageSize: ROWS_PER_PAGE,
  pageNo: INITIAL_PAGE,
  pageSize: ROWS_PER_PAGE,
  loader: false,
  activeRequestId: null,
  error: null,
};

// ==============================|| SLICE - DEDUCTOINS ||============================== //

const deductionData = createSlice({
  name: "deductionData",
  initialState,
  reducers: {
    getDeductionData(state, action) {
      let {
        deductionTableList,
        totalDeductionItems,
        totalDeductionPages,
        pageNo,
        pageSize,
      } = action.payload;
      state.deductionTableList =
        deductionTableList || initialState.deductionTableList;
      state.totalDeductionItems = totalDeductionItems;
      state.totalDeductionPages = totalDeductionPages;
      state.pageNo = pageNo || initialState.pageNo;
      state.pageSize = pageSize || initialState.pageSize;
    },
    getViewDeductionData(state, action) {
      let {
        deductionViewList,
        totalViewDeductionItems,
        totalViewDeductionPages,
        viewPageSize,
        viewPageNo,
      } = action.payload;
      state.deductionViewList =
        deductionViewList || initialState.deductionViewList;
      state.totalViewDeductionItems =
        totalViewDeductionItems || initialState.totalViewDeductionItems;
      state.totalViewDeductionPages =
        totalViewDeductionPages || initialState.totalViewDeductionPages;
      state.viewPageSize = viewPageSize;
      state.viewPageNo = viewPageNo;
    },
    getExportDeductionData(state, action) {
      let { deductionExportTableList, totalDeductionExportPages } =
        action.payload;
      state.deductionExportTableList =
        deductionExportTableList || initialState.deductionExportTableList;
      state.totalDeductionExportPages =
        totalDeductionExportPages || initialState.totalDeductionExportPages;
    },
    isLoading(state, action) {
      state.loader = action.payload;
    },
    hasError(state, action) {
      state.error = action.payload;
    },
    resetDeductions: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeductionDataThunk.pending, (state, action) => {
        state.loader = true;
        state.error = null;
        state.activeRequestId = action.meta.requestId;
      })
      .addCase(fetchDeductionDataThunk.fulfilled, (state, action) => {
        if (state.activeRequestId === action.meta.requestId) {
          const {
            allTableRows,
            totalTableRowsCount,
            totalPageCount,
            page,
            pageSize,
          } = action.payload;

          state.deductionTableList =
            allTableRows || initialState.deductionTableList;
          state.totalDeductionItems = totalTableRowsCount;
          state.totalDeductionPages = totalPageCount;
          state.pageNo = page || initialState.pageNo;
          state.pageSize = pageSize || initialState.pageSize;

          state.loader = false;
          state.activeRequestId = null;
        }
      })
      .addCase(fetchDeductionDataThunk.rejected, (state, action) => {
        state.error = (action.payload as any) ?? action.error;

        if (state.activeRequestId === action.meta.requestId) {
          state.loader = false;
          state.activeRequestId = null;
        }
      })
      .addCase(fetchViewDeductionDataThunk.pending, (state, action) => {
        state.loader = true;
        state.error = null;
        state.activeRequestId = action.meta.requestId;
      })
      .addCase(fetchViewDeductionDataThunk.fulfilled, (state, action) => {
        if (state.activeRequestId === action.meta.requestId) {
          const {
            deductionViewList,
            totalViewDeductionItems,
            totalViewDeductionPages,
            viewPageNo,
            viewPageSize,
          } = action.payload;

          state.deductionViewList =
            deductionViewList || initialState.deductionViewList;
          state.totalViewDeductionItems =
            totalViewDeductionItems || initialState.totalViewDeductionItems;
          state.totalViewDeductionPages =
            totalViewDeductionPages || initialState.totalViewDeductionPages;
          state.viewPageNo = viewPageNo || initialState.viewPageNo;
          state.viewPageSize = viewPageSize || initialState.viewPageSize;

          state.loader = false;
          state.activeRequestId = null;
        }
      })
      .addCase(fetchViewDeductionDataThunk.rejected, (state, action) => {
        state.error = (action.payload as any) ?? action.error;
        if (state.activeRequestId === action.meta.requestId) {
          state.loader = false;
          state.activeRequestId = null;
        }
      });
  },
});




export default deductionData.reducer;

export const {
  getDeductionData,
  getViewDeductionData,
  getExportDeductionData,
  resetDeductions,
} = deductionData.actions;

// ==============================|| GET ALL DEDUCTIONS ||============================== //

type FetchDeductionArgs = {
  page: any;
  pageSize: any;
  companyId: string;
  fromDate: any;
  toDate: any;
  pay_period_id: any;
  schedule_id: string;
  business_unit_id: string;
  deduction_status: string | null;
  signal?: AbortSignal;
};

let deductionAbortController: AbortController | null = null;

export const fetchDeductionDataThunk = createAsyncThunk(
  "deductionData/fetchDeductionData",
  async (args: FetchDeductionArgs, { rejectWithValue, signal }) => {
    const abortSignal = args.signal ?? signal;

    const {

      page,
      pageSize,
      companyId,
      fromDate,
      toDate,
      pay_period_id,
      schedule_id,
      business_unit_id,
      deduction_status,
    } = args;

    const startDate = !fromDate ? "" : fromDate;
    const endDate = !toDate ? "" : toDate;
    const size = pageSize < 5 ? 10 : pageSize;
    const payPeriodId = pay_period_id ? pay_period_id : "";
    const scheduleId = schedule_id ? schedule_id : "";
    const businessUnitId = business_unit_id ? business_unit_id : "";
    let deductionStatus: boolean | string;
    if (deduction_status === T.SENT) {
      deductionStatus = true;
    } else if (deduction_status === T.UNSENT) {
      deductionStatus = false;
    } else {
      deductionStatus = "";
    }

    const response = await authApi.get(
      `/v2/api/companies/DeductionFiles?company_id=${companyId}&page=${page}&pageSize=${size}&from_date=${startDate}&to_date=${endDate}&pay_period_id=${payPeriodId}&schedule_id=${scheduleId}&business_unit_id=${businessUnitId}&deduction_status=${deductionStatus}`,
      { signal: abortSignal }
    );



    if (!response?.data) return rejectWithValue({ message: "No data" });

    const { allTableRows, totalTableRowsCount, totalPageCount } =
      getCurrentTableParams(get(response, "data", {}));

    return {
      allTableRows,
      totalTableRowsCount,
      totalPageCount,
      page,
      pageSize: size,
    };
  }
);

export function fetchDeductionData(
  page: any,
  pageSize: any,
  companyId: string,
  fromDate: any,
  toDate: any,
  pay_period_id: any,
  schedule_id: string,
  business_unit_id: string,
  deduction_status: string | null
) {
  return async (dispatchFn: any, getState: any) => {
    if (deductionAbortController) {
      deductionAbortController.abort();
    }

    deductionAbortController = new AbortController();

    await dispatchFn(
      fetchDeductionDataThunk({
        page,
        pageSize,
        companyId,
        fromDate,
        toDate,
        pay_period_id,
        schedule_id,
        business_unit_id,
        deduction_status,
        signal: deductionAbortController.signal,
      })
    );
  };
}

// ==============================|| VIEW DEDUCTIONS ||============================== //

type FetchViewDeductionArgs = {
  page: any;
  pageSize: any;
  companyId: any;
  payPeriodId: any;
  deductionStatus: any;
  signal?: AbortSignal;
};

let viewDeductionAbortController: AbortController | null = null;

export const fetchViewDeductionDataThunk = createAsyncThunk(
  "deductionData/fetchViewDeductionData",
  async (args: FetchViewDeductionArgs, { rejectWithValue, signal }) => {
    const abortSignal = args.signal ?? signal;

    const { page, pageSize, companyId, payPeriodId, deductionStatus } = args;

    const size = pageSize < 5 ? 10 : pageSize;

    const response = await authApi.get(
      `/v2/api/companies/${companyId}/DeductionFiles/${payPeriodId}?page=${page}&pageSize=${size}&deduction_type=${deductionStatus}`,
      { signal: abortSignal }
    );



    if (!response?.data) {
      return rejectWithValue({ message: "No data" });
    }

    const { totalItem, totalPage, page: serverPage, result, pageSize: serverPageSize } =
      response.data;

    return {
      deductionViewList: result,
      totalViewDeductionItems: totalItem,
      totalViewDeductionPages: totalPage,
      viewPageNo: serverPage,
      viewPageSize: serverPageSize,
    };
  }
);

// New thunk-based function (latest request wins is handled via requestId gating in extraReducers)
export function fetchViewDeductionData(
  page: any,
  pageSize: any,
  companyId: any,
  payPeriodId: any,
  deductionStatus: any
) {
  return async (dispatchFn: any) => {
    if (viewDeductionAbortController) {
      viewDeductionAbortController.abort();
    }

    viewDeductionAbortController = new AbortController();

    await dispatchFn(
      fetchViewDeductionDataThunk({
        page,
        pageSize,
        companyId,
        payPeriodId,
        deductionStatus,
        signal: viewDeductionAbortController.signal,
      })
    );
  };
}

// ==============================|| RUN DEDUCTIONS ||============================== //

export function runDeduction(companyId: any, payPeriodId: any) {
  return async () => {
    const response = await authApi.post(
      `/v2/api/companies/DeductionFiles/runDeduction`,
      {
        companyId,
        payPeriodId,
      }
    );
    if (response.data) {
      toast(T.DEDUCTION_RUN_SUCCESSFULLY, { variant: "success" });
    }
  };
}

// ==============================|| REVERSE DEDUCTIONS ||============================== //

export function reverseDeduction(companyId: string, payPeriodId: string) {
  return async () => {
    const response = await authApi.post(
      `/v2/api/companies/${companyId}/DeductionFiles/${payPeriodId}/undo`
    );
    if (response.data) {
      toast(T.DEDUCTION_REVERSED_SUCCESSFULLY, { variant: "success" });
    }
  };
}
